/**
 * §4.3.2 — resolve getters and render casts in FIELD VALUES to literals.
 *
 * A field may take its value by reference instead of restating it:
 *
 *     widget.subheader: @@team              // this node's team, whatever it is
 *     widget.footer: @repo.url              // a sibling field
 *     widget.color: colorize(@@team)        // a colour derived from that value
 *
 * The grammar is the one a view's `style` body already uses, so prose (§4.10.3),
 * views and fields all read values the same way — one surface, not three.
 *
 * **Runs after cascade, per node.** That is the whole point: a type declaring
 * `cascade widget.color: colorize(@@team)` colours each descendant by ITS OWN
 * team, so the expression has to be evaluated where it lands, not where it was
 * written. By the time `applyCascade` is done, every inherited field and aspect
 * is stamped on the node, so every lookup here is local.
 *
 * **It literalises `value` and keeps the expression in `from`.** Downstream —
 * widget spec, scene, live board, SVG export, MCP, CLI, LSP — keeps reading
 * plain values and needs no knowledge of this pass at all. `from` is there for
 * anything writing source text back (the WYSIWYG inspector, `archlang set`),
 * which must never freeze a model-wide rule into the constant it happened to
 * produce on one node.
 */

import { makeDiag } from "./diagnostics/catalog.js";
import { decorationLookup } from "./decorations.js";
import type { DecorationRead, DecorationRegistry } from "./decorations.js";
import { aspectLookup, fieldLookup } from "./description.js";
import { isCssColor, swatchFor } from "./palette.js";
import type { Expr } from "./selector-ast.js";
import { EXPRESSION_FUNCTIONS, exprText, SET_RELATIVE_FUNCTIONS, valueText } from "./selector-ast.js";
import type { ResolvedInterface, ResolvedModel, ResolvedModule, ResolvedSurface } from "./resolver.js";
import { assignHeat, NEUTRAL_NO_DATA } from "./view-style.js";
import type { Body, Diagnostic, FieldDecl, InterfaceBody, SourceSpan, Value } from "./types.js";

// =============================================================================
// Diagnostic codes
// =============================================================================

/** A colour slot holds a name that is not in the palette (`crimsn`). */
export const FIELD_UNKNOWN_COLOR = "FIELD_UNKNOWN_COLOR";
/** A colour slot holds a plain value, not a colour — `colorize(…)` it. */
export const FIELD_COLOR_NEEDS_CAST = "FIELD_COLOR_NEEDS_CAST";
/** `@x` / `@@x` resolves to nothing on this node. */
export const FIELD_GETTER_MISSING = "FIELD_GETTER_MISSING";
/** Field values reference each other in a loop. */
export const FIELD_REF_CYCLE = "FIELD_REF_CYCLE";
/** A cast that needs a value universe, used in an element body. */
export const FIELD_CAST_NEEDS_VIEW = "FIELD_CAST_NEEDS_VIEW";
/** A cast got an argument it cannot use (a list, the wrong arity). */
export const FIELD_CAST_BAD_ARG = "FIELD_CAST_BAD_ARG";
/** An expression shape that means nothing in a field value (`exists (…)`). */
export const FIELD_EXPR_UNSUPPORTED = "FIELD_EXPR_UNSUPPORTED";

/**
 * The colour-typed slots. A value landing in one of these must BE a colour — a
 * palette name, a literal CSS colour, or the result of a colour cast — so a
 * typo or a raw team name is caught here instead of being handed to the renderer
 * as a paint instruction.
 */
const COLOR_FIELDS: ReadonlySet<string> = new Set([
  "accent",
  "widget.color",
  "widget.badgeColor",
  "widget.bg",
  // Read as a colour by scene/board/card-input.ts like the rest. Leaving it out
  // let an unresolvable string reach `stop-color=` in exported SVG.
  "widget.accent",
]);

// =============================================================================
// Entry point
// =============================================================================

/**
 * Literalise every expression-valued field in the model.
 *
 * Called from `applyCascade`, so it is impossible to obtain a cascaded model
 * whose fields still carry expressions. `diagnostics`, when given, collects what
 * could not be resolved; without it the diagnostics are dropped, which is what
 * the render-only consumers want.
 */
export function resolveFieldExprs(model: ResolvedModel, diagnostics?: Diagnostic[]): ResolvedModel {
  const sink: Diagnostic[] = [];
  const reg = model.decorationRegistry;
  const modules = mapSame(model.modules, (m) => resolveModule(m, sink, reg));
  if (diagnostics) diagnostics.push(...dedupeBySite(sink));
  return modules === model.modules ? model : { ...model, modules };
}

/** The `@` axis reader for one node's decorations — shared with the description
 *  axis so the decoration-wins rule (§4.3.1) has a single owner. */
type DecoRead = (path: readonly string[]) => DecorationRead;

/**
 * One broken DECLARATION → one diagnostic.
 *
 * A cascaded field is resolved once per element it lands on, so a single
 * `cascade widget.color: colorize(@@team)` that resolves nowhere would report
 * the same source line once per module in the subtree — and since aspects
 * cascade too, a declaring module that cannot resolve it guarantees that none
 * of its descendants can. The author has one line to fix and gets one
 * complaint. Anything whose message genuinely differs per element (a value that
 * needs a cast names the value it found) keeps its own entry.
 */
function dedupeBySite(diags: readonly Diagnostic[]): Diagnostic[] {
  const seen = new Set<string>();
  const out: Diagnostic[] = [];
  for (const d of diags) {
    const s = d.source;
    const key = `${d.code}|${d.message}|${s?.file}|${s?.start.offset}|${s?.end.offset}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(d);
  }
  return out;
}

/**
 * `map`, but returns the ORIGINAL array when every element came back identical.
 *
 * Load-bearing, not a micro-optimisation: the incremental compiler decides what
 * to re-validate by comparing module identity across states, so a pass that
 * rebuilds untouched nodes turns an O(1) edit into an O(N) revalidation of the
 * whole model.
 */
function mapSame<T>(items: readonly T[], f: (item: T) => T): readonly T[] {
  let changed = false;
  const out = items.map((item) => {
    const next = f(item);
    if (next !== item) changed = true;
    return next;
  });
  return changed ? out : items;
}

function resolveModule(
  m: ResolvedModule,
  sink: Diagnostic[],
  reg: DecorationRegistry | undefined,
): ResolvedModule {
  const deco = decorationLookup(m.decl.decorations, reg);
  const body = resolveBody(m.body, sink, undefined, deco);
  // §4.5.B: a module's `cascade` FIELDS stop at the module boundary — they used
  // to leak into every interface and paint an empty ghost node on each edge —
  // but "Aspects still reach the whole subtree". The cascade materialises
  // inherited aspects onto nested MODULES (so `m.body.aspects` is already the
  // effective set) and deliberately NOT onto surfaces/interfaces, whose own list
  // stays honest about what was declared there. So the walk hands the module's
  // effective aspects down as the scope those getters resolve against: without
  // it, `rest_read create { widget.color: colorize(@@team) }` cannot see the
  // team of the module that owns it, and an edge cannot be coloured by its
  // owner.
  const surfaces = mapSame(m.surfaces, (s) => resolveSurface(s, sink, reg, m.body.aspects));
  const interfaces = mapSame(m.interfaces, (i) => resolveInterface(i, sink, reg, m.body.aspects));
  const modules = mapSame(m.modules, (c) => resolveModule(c, sink, reg));
  if (body === m.body && surfaces === m.surfaces && interfaces === m.interfaces && modules === m.modules) {
    return m;
  }
  return { ...m, body, surfaces, interfaces, modules };
}

function resolveSurface(
  s: ResolvedSurface,
  sink: Diagnostic[],
  reg: DecorationRegistry | undefined,
  inherited?: InterfaceBody["aspects"]
): ResolvedSurface {
  const body = resolveBody(s.body, sink, inherited, decorationLookup(s.decl.decorations, reg));
  // A facet nests (surface → surface → interface), so the scope grows as it
  // descends; nearest declaration wins, which `aspectScope` gets by ordering.
  const scope = aspectScope(s.body.aspects, inherited);
  const surfaces = mapSame(s.surfaces, (c) => resolveSurface(c, sink, reg, scope));
  const interfaces = mapSame(s.interfaces, (i) => resolveInterface(i, sink, reg, scope));
  if (body === s.body && surfaces === s.surfaces && interfaces === s.interfaces) return s;
  return { ...s, body, surfaces, interfaces };
}

function resolveInterface(
  i: ResolvedInterface,
  sink: Diagnostic[],
  reg: DecorationRegistry | undefined,
  inherited?: InterfaceBody["aspects"]
): ResolvedInterface {
  const deco = decorationLookup(i.decl.decorations, reg);
  const fields = resolveFields(i.body.fields, i.body.aspects, sink, inherited, deco);
  return fields === i.body.fields ? i : { ...i, body: { ...i.body, fields } };
}

function resolveBody(
  body: Body,
  sink: Diagnostic[],
  inherited?: InterfaceBody["aspects"],
  deco?: DecoRead,
): Body {
  const fields = resolveFields(body.fields, body.aspects, sink, inherited, deco);
  return fields === body.fields ? body : { ...body, fields };
}

/** Ancestor aspects first, the element's own LAST — `aspectLookup` keys by
 *  dotted path into a Map, so the last writer wins and the nearest declaration
 *  is the one a getter sees. Returns the own list untouched when there is
 *  nothing to inherit, so the common body allocates nothing. */
function aspectScope(
  own: InterfaceBody["aspects"],
  inherited: InterfaceBody["aspects"] | undefined
): InterfaceBody["aspects"] {
  if (!inherited || inherited.length === 0) return own;
  if (own.length === 0) return inherited;
  return [...inherited, ...own];
}

// =============================================================================
// Per-node resolution
// =============================================================================

/**
 * Resolve one node's fields. Fields may reference each other, so resolution is
 * lazy + memoised with an in-progress set — that is also how a cycle is caught
 * (`a: @b`, `b: @a`) rather than recursing until the stack gives out.
 */
function resolveFields(
  fields: readonly FieldDecl[],
  aspects: InterfaceBody["aspects"],
  sink: Diagnostic[],
  inherited?: InterfaceBody["aspects"],
  readDeco?: DecoRead,
): readonly FieldDecl[] {
  // Nothing to resolve and no colour to validate — the overwhelmingly common
  // body, kept allocation-free (this runs over every node of every model).
  const interesting = fields.some(
    (f) => f.value !== undefined && (f.value.kind === "expr" || COLOR_FIELDS.has(f.path.join(".")))
  );
  if (!interesting) return fields;

  // Merged only past the gate above, so a body with nothing to resolve still
  // allocates nothing on the hot path.
  const getAspect = aspectLookup(aspectScope(aspects, inherited));
  const rawField = fieldLookup(fields);
  const byPath = new Map(fields.map((f) => [f.path.join("."), f] as const));
  const resolved = new Map<string, Value | undefined>();
  const inProgress = new Set<string>();

  /** A field's value with any expression already resolved (memoised). */
  function fieldValue(path: readonly string[]): Value | undefined {
    const key = path.join(".");
    if (resolved.has(key)) return resolved.get(key);
    const decl = byPath.get(key);
    if (!decl?.value) return undefined;
    if (decl.value.kind !== "expr") return decl.value;
    if (inProgress.has(key)) {
      sink.push(
        makeDiag("error", decl.source, FIELD_REF_CYCLE, "field.refCycle", { path: key })
      );
      resolved.set(key, undefined);
      return undefined;
    }
    inProgress.add(key);
    // A getter that arrived from a TYPE (a stdlib widget's optional
    // `footer: @@domain`) is a slot offer, not a claim that the value exists —
    // so a miss is silent. Only a getter authored on this element is worth a
    // diagnostic, where it means a typo or a stale reference.
    //
    // `fromTemplate` is the whole test, and testing `cascade` too was a bug:
    // `widget.color` ships from `BUILTIN_MODULE_BODY` as a CASCADING default,
    // so an author's own `widget.color: colorize(@@team)` merges over it and
    // keeps that flag — the value is theirs, the modifier is the builtin's.
    // Reading the modifier as provenance made the card's flagship slot the one
    // place a typo'd getter stayed silent. An inherited cascade already carries
    // `fromTemplate`, so it stays silent on its own.
    const authored = !decl.fromTemplate;
    const value = evalExprValue(decl.value.expr, key, decl.value.source ?? decl.source, authored);
    inProgress.delete(key);
    resolved.set(key, value);
    return value;
  }

  /** Evaluate an expression in `slot`'s value position → a literal, or
   *  undefined when it could not be resolved (always diagnosed). */
  function evalExprValue(
    expr: Expr,
    slot: string,
    source: SourceSpan | undefined,
    authored: boolean
  ): Value | undefined {
    const isColorSlot = COLOR_FIELDS.has(slot);

    if (expr.kind === "call") {
      const text = castToColor(expr, slot, source, authored);
      return text === undefined ? undefined : { kind: "string", text, source };
    }

    const text = plainText(expr, source, authored);
    if (text === undefined) return undefined;

    // A getter's value lands verbatim — that is what makes a text leaf work
    // (`widget.subheader: @@team`). In a COLOUR slot it must itself be a colour:
    // a bare team name is not one, and deriving a colour from it is an explicit
    // `colorize(…)`, never a silent guess.
    if (isColorSlot && !isCssColor(text)) {
      sink.push(
        makeDiag("error", source, FIELD_COLOR_NEEDS_CAST, "field.colorNeedsCast", {
          path: slot,
          expr: exprText(expr),
          value: text,
        })
      );
      return undefined;
    }
    return { kind: "string", text, source };
  }

  /** A getter or literal → its text. Anything else is not a field value. */
  function plainText(expr: Expr, source: SourceSpan | undefined, authored: boolean): string | undefined {
    if (expr.kind === "literal") return literalText(expr.value);
    if (expr.kind !== "getter") {
      sink.push(
        makeDiag("error", source ?? expr.source, FIELD_EXPR_UNSUPPORTED, "field.exprUnsupported", {
          expr: exprText(expr),
        })
      );
      return undefined;
    }
    // `@` reads the field axis, `@@` the aspect axis — the sigil names the axis,
    // exactly as in a description (§4.10.3) or a selector.
    //
    // Within `@`, a DECLARED decoration wins over a field of the same name
    // (§4.3.1) — the rule that makes such a field unreachable through a getter.
    // Same precedence as the selector axis, so `@planned.from` means one thing
    // wherever it is written.
    const deco = expr.sigil === "@@" ? undefined : readDeco?.(expr.path);
    const value =
      expr.sigil === "@@"
        ? getAspect(expr.path)
        : deco?.hit
          ? deco.value
          : fieldValue(expr.path);
    if (value === undefined) {
      // A field getter may point at a field that exists but failed to resolve;
      // that failure is already diagnosed, so don't pile a second one on.
      const alreadyDiagnosed = expr.sigil === "@" && rawField(expr.path) !== undefined;
      if (authored && !alreadyDiagnosed) {
        sink.push(
          makeDiag("warning", source ?? expr.source, FIELD_GETTER_MISSING, "field.getterMissing", {
            getter: exprText(expr),
          })
        );
      }
      return undefined;
    }
    return literalText(value);
  }

  /** A resolved literal → the text a field value carries. A list has no single
   *  text, so it is not usable through a getter. */
  function literalText(v: Value): string | undefined {
    switch (v.kind) {
      case "string":
      case "identifier":
        return v.text;
      case "number":
      case "boolean":
        return String(v.value);
      case "ref":
        return v.path.join(".");
      case "list":
      case "expr":
        return undefined;
    }
  }

  /** A render cast → a concrete colour token (a palette name or a hex). */
  function castToColor(
    call: Extract<Expr, { kind: "call" }>,
    slot: string,
    source: SourceSpan | undefined,
    authored: boolean
  ): string | undefined {
    if (!EXPRESSION_FUNCTIONS.has(call.fn)) {
      sink.push(
        makeDiag("error", source, FIELD_EXPR_UNSUPPORTED, "field.exprUnsupported", {
          expr: exprText(call),
        })
      );
      return undefined;
    }
    if (SET_RELATIVE_FUNCTIONS.has(call.fn)) {
      // `distinct` assigns colours across a value SET; an element body has no
      // set, so it would silently mean "always ring slot 0".
      sink.push(
        makeDiag("error", source, FIELD_CAST_NEEDS_VIEW, "field.castNeedsView", { cast: call.fn })
      );
      return undefined;
    }

    const [argE, minE, maxE] = call.args;
    if (!argE) {
      sink.push(makeDiag("error", source, FIELD_CAST_BAD_ARG, "field.castBadArg", { cast: call.fn }));
      return undefined;
    }

    if (call.fn === "heat") {
      const min = numberArg(minE);
      const max = numberArg(maxE);
      if (min === undefined || max === undefined) {
        sink.push(makeDiag("error", source, FIELD_CAST_BAD_ARG, "field.castBadArg", { cast: "heat" }));
        return undefined;
      }
      const text = plainText(argE, source, authored);
      // A getter that resolved to nothing already carries its own diagnostic.
      if (text === undefined) return undefined;
      // A value the ramp cannot place is refused, exactly as `css(…)` refuses a
      // value that is not a colour. Feeding it through anyway painted the
      // neutral "no data" grey: honest in a VIEW, where a legend row spells the
      // bucket out, but a field has no legend — the author would just see a grey
      // node and no reason for it. `Number("")` is 0, so blank is not a number
      // either.
      const n = text.trim() === "" ? NaN : Number(text);
      if (!Number.isFinite(n)) {
        sink.push(makeDiag("error", source, FIELD_CAST_BAD_ARG, "field.castBadArg", { cast: "heat" }));
        return undefined;
      }
      // Reuse the view ramp so a field and a view agree on the same number.
      const asn = assignHeat(new Map([["_", n]]), min, max, slot, slot);
      return asn.colors.get("_") ?? NEUTRAL_NO_DATA;
    }

    const text = plainText(argE, source, authored);
    if (text === undefined) return undefined;

    if (call.fn === "colorize") return swatchFor(text);

    // `css(…)` — the value IS the colour, so it has to parse as one.
    if (!isCssColor(text)) {
      sink.push(
        makeDiag("warning", source, FIELD_UNKNOWN_COLOR, "field.unknownColor", { value: text })
      );
      return undefined;
    }
    return text;
  }

  function numberArg(e: Expr | undefined): number | undefined {
    if (e?.kind !== "literal" || e.value.kind !== "number") return undefined;
    return e.value.value;
  }

  const out: FieldDecl[] = [];
  let changed = false;
  for (const f of fields) {
    if (f.value?.kind !== "expr") {
      // A literal in a colour slot is validated too — a mistyped palette name
      // must not reach the renderer as a paint instruction.
      if (f.value && COLOR_FIELDS.has(f.path.join("."))) {
        const text = literalText(f.value);
        if (text !== undefined && !isCssColor(text)) {
          sink.push(
            makeDiag("warning", f.source, FIELD_UNKNOWN_COLOR, "field.unknownColor", { value: text })
          );
          changed = true;
          continue;
        }
      }
      out.push(f);
      continue;
    }
    changed = true;
    const value = fieldValue(f.path);
    // An unresolved value is DROPPED, never kept as its own source text: a card
    // with no colour falls back to its type's, a card painted "@@team" does not.
    if (value === undefined) continue;
    out.push({ ...f, value, from: f.value.expr });
  }
  // Identity out when nothing moved — see `mapSame`: the incremental compiler
  // reads module identity to decide what needs re-validating.
  return changed ? out : fields;
}

/** The source text of a field's value — the expression when it came from one.
 *  For anything writing `.arch` text back (§WYSIWYG). */
export function fieldSourceText(f: FieldDecl): string | undefined {
  if (f.from) return exprText(f.from);
  return f.value ? valueText(f.value) : undefined;
}

/**
 * A field value as the text an EDITOR should show — and therefore round-trip.
 *
 * A string shows its content without quotes (the editor adds them back on
 * write); everything else shows its source form, so a getter or cast reads back
 * as the RULE (`colorize(@@team)`) instead of the constant it resolved to on
 * this one element.
 */
export function fieldEditorText(v: Value): string {
  return v.kind === "string" ? v.text : valueText(v);
}

/** Does this editor text denote a getter or cast rather than a literal? Used by
 *  the write path so `@@team` is not quoted into the string `"@@team"`. */
export function isValueExprText(raw: string): boolean {
  const t = raw.trim();
  if (t.startsWith("@")) return true;
  const call = /^([A-Za-z_]\w*)\s*\(/.exec(t);
  return call !== null && EXPRESSION_FUNCTIONS.has(call[1]!);
}
